import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AnalysisScope, isWholeOrganisation, modelsInScope, scopeForLinqset } from '../analysis/scope';
import { Change, changeFormat, changeFrom, measure } from '../analysis/comparison';
import { ContextGraphButtonComponent } from '../analysis/context-graph-button.component';
import { AnalysisEdge, AnalysisNode } from '../common/analysis-model';
import { GraphData, Linqset, ORGANISATION_MODEL_KEY } from '../common/linqset-model';
import { createInsightsFilter, DEFAULT_CURRENCY, defaultCapacityHours, getCurrency } from '../common/organisation';
import { FormattingService } from '../linq-utils/formatting/formatting.service';
import { MODEL_API, RepositoryService } from '../linq-utils/repository.service';
import { InsightsService } from '../model-services/insights.service';
import { OrganisationService } from '../organisation/organisation.service';
import { buildWorkforceReport, WORKFORCE_STATUS_LABELS, FindingSeverity, PersonSummary, RoleSummary, WorkforceMatch, WorkforceMatrix, WorkforceReport, WorkforceStatus } from './workforce-model';

// the headline figures, as they're compared
const workforceFigures = (r: WorkforceReport) => ({
    people: measure(r.totals.headcount, 'none', 'count'),
    cost: measure(r.totals.cost, 'down', 'money'),
    utilisation: r.totals.utilisation === null ? undefined : measure(r.totals.utilisation * 100, 'none', 'percent'),
    spare: measure(r.totals.spareHours, 'none', 'hours'),
    over: measure(r.totals.overRoles, 'down', 'count'),
    friction: r.totals.frictionCost ? measure(r.totals.frictionCost, 'down', 'money') : measure(r.totals.frictionHours, 'down', 'hours'),
});

const COMPONENT = 'Workforce report';


const SEVERITY: Record<FindingSeverity, { icon: string; label: string }> = {
    critical: { icon: 'error', label: 'Act now' },
    warning: { icon: 'warning', label: 'Look at' },
    info: { icon: 'info', label: 'Data' },
};

// the single-hue ramp the heatmaps use, light (little) to dark (most)
const RAMP = ['#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#184f95', '#0d366b'];

// Who does the work across the organisation: people and roles, their cost and workload against capacity, who they
// pass work to, the systems they use, and who could help where a role is short. What needs attention comes first
@Component({
    selector: 'app-workforce-report',
    standalone: true,
    imports: [DecimalPipe, NgTemplateOutlet, FormsModule, MatButtonModule, MatButtonToggleModule, MatIconModule, MatProgressSpinnerModule, MatSlideToggleModule, MatTooltipModule, ContextGraphButtonComponent],
    template: `
        @if (loading) {
        <div class="status" role="status"><mat-spinner diameter="22"></mat-spinner><span>Building the workforce report…</span></div>
        }@else if (error) {
        <p class="status error" role="alert">{{ error }}</p>
        }@else if (report; as r) {
        <div class="workforce" data-testid="workforce-report">
            <header class="intro">
                <h2>Workforce</h2>
                @if (r.scoped) {
                <p data-testid="workforce-scope">The people and roles who do the work in <strong>{{ scopeName || 'the models shown' }}</strong>: what they cost, their work there
                    against their capacity, who they pass work to and the systems they use. A role's capacity also has to cover its work in
                    the organisation's other models, so that work is counted too and shown separately.</p>
                }@else {
                <p>Everyone and every role in the organisation: what they cost, how much work they have against their capacity,
                    who they pass work to and the systems they use. Built from the organisation model and the work in every model.</p>
                }
            </header>

            <!-- the headline figures -->
            <section class="tiles" aria-label="Headline figures">
                <div class="tile">
                    <span class="label"><mat-icon aria-hidden="true">groups</mat-icon>People</span>
                    <strong>{{ r.totals.headcount | number }}</strong>@if (vs('people'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">in {{ r.totals.roles }} {{ r.totals.roles === 1 ? 'role' : 'roles' }}</span>
                </div>
                <div class="tile">
                    <span class="label"><mat-icon aria-hidden="true">payments</mat-icon>Annual cost</span>
                    <strong>{{ money(r.totals.cost) }}</strong>@if (vs('cost'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">people and roles</span>
                </div>
                <div class="tile" matTooltip="Work as a share of capacity, for the roles whose capacity is set. Work that isn't modelled isn't counted">
                    <span class="label"><mat-icon aria-hidden="true">speed</mat-icon>Utilisation</span>
                    <strong [class]="'status-text ' + overallStatus(r)">{{ r.totals.utilisation === null ? '–' : (r.totals.utilisation * 100 | number: '1.0-0') + '%' }}</strong>@if (vs('utilisation'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    @if (r.totals.otherHours) {
                    <span class="sub">{{ hours(r.totals.hours) }} {{ hereLabel.toLowerCase() }} + {{ hours(r.totals.otherHours) }} in other models, of {{ hours(r.totals.capacity) }} h</span>
                    }@else {
                    <span class="sub">{{ hours(r.totals.hours) }} of {{ hours(r.totals.capacity) }} hours of modelled work</span>
                    }
                </div>
                <div class="tile" matTooltip="Capacity no modelled work uses. Some of it is likely work that isn't modelled yet">
                    <span class="label"><mat-icon aria-hidden="true">hourglass_empty</mat-icon>Unused capacity</span>
                    <strong>{{ hours(r.totals.spareHours) }} h</strong>@if (vs('spare'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">about {{ money(r.totals.spareCost) }} a year</span>
                </div>
                <div class="tile" [class.alert]="r.totals.overRoles > 0">
                    <span class="label"><mat-icon aria-hidden="true">warning</mat-icon>Roles over capacity</span>
                    <strong>{{ r.totals.overRoles }}</strong>@if (vs('over'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">{{ r.totals.unknownRoles ? r.totals.unknownRoles + ' without capacity set' : r.totals.defaultRoles + ' on the full-time default' }}</span>
                </div>
                <div class="tile" [class.alert]="r.totals.frictionActions > 0" matTooltip="People's time on friction work: exception work (steps on a detour from the main flow, or marked as such) and work marked as not adding value. Friction on the EPM scorecard">
                    <span class="label"><mat-icon aria-hidden="true">alt_route</mat-icon>Friction work</span>
                    <strong>{{ r.totals.frictionCost ? money(r.totals.frictionCost) : r.totals.frictionHours ? hours(r.totals.frictionHours) + ' h' : '–' }}</strong>@if (vs('friction'); as c) {<span [class]="'chg ' + c.tone">{{ c.text }}</span>}
                    <span class="sub">{{ r.totals.frictionActions ? hours(r.totals.frictionHours) + ' hours a year in ' + r.totals.frictionActions + (r.totals.frictionActions === 1 ? ' step' : ' steps') + ', by ' + r.totals.frictionRoles + (r.totals.frictionRoles === 1 ? ' role' : ' roles') : 'no exception or wasted work' }}</span>
                </div>
            </section>

            <!-- what to look at first -->
            <section class="panel" aria-labelledby="wf-findings">
                <div class="panel-head">
                    <h3 id="wf-findings">Look at these first</h3>
                    @if (r.findings.length) {
                    <span class="counts">
                        @for (c of severityCounts; track c.severity) {<span [class]="'pill ' + c.severity">{{ c.count }} {{ severity(c.severity).label.toLowerCase() }}</span>}
                    </span>
                    }
                </div>
                @if (r.findings.length) {
                <ul class="findings" data-testid="workforce-findings">
                    @for (f of (allFindings ? r.findings : r.findings.slice(0, 5)); track $index) {
                    <li [class]="'finding ' + f.severity">
                        <mat-icon [attr.aria-label]="severity(f.severity).label">{{ severity(f.severity).icon }}</mat-icon>
                        <div>
                            <strong class="with-context">{{ f.title }} @if (contextScope && f.roleKey) {<app-context-graph-button [organisation]="linqset.organisation" [scope]="contextScope" [request]="{subject: {kind: 'role', key: f.roleKey!, name: f.title}}" [name]="f.title"></app-context-graph-button>}</strong>
                            <p>{{ f.detail }}</p>
                        </div>
                        <span [class]="'pill ' + f.severity">{{ severity(f.severity).label }}</span>
                    </li>
                    }
                </ul>
                @if (r.findings.length > 5) {
                <button mat-button class="more" (click)="allFindings = !allFindings">
                    <mat-icon>{{ allFindings ? 'expand_less' : 'expand_more' }}</mat-icon>{{ allFindings ? 'Show fewer' : 'Show all ' + r.findings.length }}
                </button>
                }
                }@else {
                <p class="none">Nothing stands out: roles have the capacity for their work and no one is overcommitted.</p>
                }
            </section>

            <div class="two">
                <!-- utilisation by role -->
                <section class="panel" aria-labelledby="wf-utilisation">
                    <h3 id="wf-utilisation">Workload against capacity, by role</h3>
                    <p class="wf-hint">Work a year as a share of the role's capacity. The line marks 100%.@if (hasOtherWork) { Striped: its work in the organisation's other models, which the same capacity covers.}@if (r.totals.defaultRoles) { * Capacity is the organisation's full-time year.}</p>
                    <div class="legend">
                        @for (s of statuses; track s) {<span><i [class]="'swatch ' + s"></i>{{ statusLabel(s) }}</span>}
                        @if (hasOtherWork) {<span><i class="swatch here"></i>{{ hereLabel }}</span><span><i class="swatch other"></i>In other models</span>}
                    </div>
                    <ol class="bars" data-testid="workforce-utilisation">
                        @for (role of (allRoles ? knownRoles : knownRoles.slice(0, 12)); track role.key) {
                        <li [matTooltip]="roleTip(role)">
                            <span class="name with-context" [title]="role.name"><span class="ellipsis">{{ role.name }}@if (role.capacityFromDefault) {<span class="default-mark" aria-label="capacity from the full-time default">*</span>}</span>@if (contextScope) {<app-context-graph-button [organisation]="linqset.organisation" [scope]="contextScope" [request]="{subject: {kind: 'role', key: role.key, name: role.name}}" [name]="role.name"></app-context-graph-button>}</span>
                            <span class="track">
                                <span [class]="'fill ' + role.status" [style.width.%]="barWidth(hereShare(role))"></span>
                                @if (role.otherHours && role.capacity) {
                                <span [class]="'fill other ' + role.status" [style.left.%]="barWidth(hereShare(role))" [style.width.%]="barWidth(role.utilisation) - barWidth(hereShare(role))" data-testid="workforce-other-work"></span>
                                }
                                <span class="capacity" [style.left.%]="barWidth(1)"></span>
                            </span>
                            <span class="value" [class.over]="role.status === 'over'">{{ role.utilisation! * 100 | number: '1.0-0' }}%</span>
                        </li>
                        }
                    </ol>
                    @if (knownRoles.length > 12) {
                    <button mat-button class="more" (click)="allRoles = !allRoles">
                        <mat-icon>{{ allRoles ? 'expand_less' : 'expand_more' }}</mat-icon>{{ allRoles ? 'Show fewer' : 'Show all ' + knownRoles.length + ' roles' }}
                    </button>
                    }
                    @if (!knownRoles.length) {<p class="none">No role has its capacity set, so workload can't be compared with it.</p>}
                </section>

                <!-- cost by role -->
                <section class="panel" aria-labelledby="wf-cost">
                    <h3 id="wf-cost">Where the money goes</h3>
                    <p class="wf-hint">Each role's annual cost, how much of it goes on friction work (exception work (steps on a detour from the main flow, or marked as such) and work marked as not adding value), and how much pays for capacity no modelled work uses.</p>
                    <div class="legend"><span><i class="swatch used"></i>Used by work</span><span><i class="swatch exception"></i>Friction work</span><span><i class="swatch idle"></i>Unused capacity</span></div>
                    <ol class="bars" data-testid="workforce-cost">
                        @for (role of costRoles; track role.key) {
                        <li [matTooltip]="role.name + ': ' + money(role.cost) + ' a year, ' + money(role.idleCost) + ' of it unused capacity' + (role.frictionActions ? ', ' + money(role.frictionCost) + ' on friction work' : '')">
                            <span class="name with-context" [title]="role.name"><span class="ellipsis">{{ role.name }}</span>@if (contextScope) {<app-context-graph-button [organisation]="linqset.organisation" [scope]="contextScope" [request]="{subject: {kind: 'role', key: role.key, name: role.name}}" [name]="role.name"></app-context-graph-button>}</span>
                            <span class="track">
                                <span class="fill used" [style.width.%]="costWidth(role.cost - role.idleCost - role.frictionCost)"></span>
                                <span class="fill exception" [style.left.%]="costWidth(role.cost - role.idleCost - role.frictionCost)" [style.width.%]="costWidth(role.frictionCost)"></span>
                                <span class="fill idle" [style.left.%]="costWidth(role.cost - role.idleCost)" [style.width.%]="costWidth(role.idleCost)"></span>
                            </span>
                            <span class="value">{{ money(role.cost) }}</span>
                        </li>
                        }@empty {<li class="none empty-row">No role has a cost set.</li>}
                    </ol>
                </section>
            </div>

            <!-- the value each role's work creates -->
            <section class="panel" aria-labelledby="wf-value">
                <h3 id="wf-value">The value each role creates</h3>
                <p class="wf-hint">Each role's share of the value and revenue its work creates. Each output's value is shared between the steps producing it by their work, and each step's share between the roles doing it, so these add up. Only value adding work creates any. Revenue return is the revenue it creates for each {{ currencySymbol }}1 its work costs.</p>
                @if (valueRoles.length) {
                <div class="scroll">
                    <table class="people values" data-testid="workforce-value">
                        <thead><tr><th>Role</th><th class="num">Value contribution</th><th class="num">Revenue contribution</th><th class="num">Cost of its work</th><th class="num">Revenue return</th></tr></thead>
                        <tbody>
                            @for (role of valueRoles; track role.key) {
                            <tr>
                                <td class="person">{{ role.name }}@if (role.external) {<span class="tag">External</span>}</td>
                                <td class="num"><span class="mini"><span class="fill used" [style.width.%]="share(role.contribution.value, r.totals.valueContribution)"></span></span><span class="mini-value">{{ count(role.contribution.value) }}</span></td>
                                <td class="num">{{ role.contribution.revenue ? money(role.contribution.revenue) : '–' }}</td>
                                <td class="num">{{ role.contribution.workCost ? money(role.contribution.workCost) : '–' }}</td>
                                <td class="num" [class.low]="role.contribution.revenueReturn !== null && role.contribution.revenueReturn < 1">{{ role.contribution.revenueReturn === null ? '–' : (role.contribution.revenueReturn | number: '1.1-2') + '×' }}</td>
                            </tr>
                            }
                        </tbody>
                        <tfoot><tr><td>All roles</td><td class="num">{{ count(r.totals.valueContribution) }}</td><td class="num">{{ r.totals.revenueContribution ? money(r.totals.revenueContribution) : '–' }}</td><td class="num">{{ r.totals.workCost ? money(r.totals.workCost) : '–' }}</td>
                            <td class="num">{{ r.totals.workCost && r.totals.revenueContribution ? (r.totals.revenueContribution / r.totals.workCost | number: '1.1-2') + '×' : '–' }}</td></tr></tfoot>
                    </table>
                </div>
                }@else {
                <p class="none">No role's work creates value or revenue yet: set a value or revenue on the outputs or outcomes it leads to.</p>
                }
            </section>

            <!-- people -->
            <section class="panel" aria-labelledby="wf-people">
                <div class="panel-head">
                    <h3 id="wf-people">People</h3>
                    <mat-slide-toggle [(ngModel)]="peopleToLookAt" data-testid="workforce-people-filter">Only those to look at</mat-slide-toggle>
                </div>
                <div class="scroll">
                    <table class="people" data-testid="workforce-people">
                        <thead><tr><th>Person</th><th>Roles</th><th>Utilisation</th><th class="num">Hours a year</th><th class="num">Cost</th><th>Look at</th></tr></thead>
                        <tbody>
                            @for (p of shownPeople; track p.key) {
                            <tr>
                                <td class="person">{{ p.name }}@if (p.team) {<span class="tag">Team of {{ p.headcount }}</span>}</td>
                                <td class="roles">@for (role of p.roles; track role.key) {<span class="chip">{{ role.name }}@if (p.roles.length > 1) {<span class="share">{{ role.share * 100 | number: '1.0-0' }}%</span>}</span>}@empty {<span class="muted">–</span>}</td>
                                <td class="nowrap">
                                    @if (p.utilisation !== null) {
                                    <span class="mini" [matTooltip]="statusLabel(p.status)"><span [class]="'fill ' + p.status" [style.width.%]="miniWidth(p.utilisation)"></span></span>
                                    <span class="mini-value" [class.over]="p.status === 'over'">{{ p.utilisation * 100 | number: '1.0-0' }}%</span>
                                    }@else {<span class="muted">–</span>}
                                </td>
                                <td class="num">{{ hours(p.hours) }} <span class="muted">of {{ hours(p.capacity) }}</span></td>
                                <td class="num">{{ money(p.cost) }}</td>
                                <td>@for (flag of p.flags; track flag) {<span class="flag" [class.serious]="flag === 'Over capacity'">{{ flag }}</span>}</td>
                            </tr>
                            }@empty {
                            <tr><td colspan="6" class="none">{{ r.people.length ? 'No one needs looking at.' : r.scoped ? 'No one in the organisation model fills these roles: assign people to them there to see them here.' : 'There are no people in the organisation model: add people and assign them roles to see them here.' }}</td></tr>
                            }
                        </tbody>
                    </table>
                </div>
            </section>

            <!-- how roles work together -->
            <section class="panel" aria-labelledby="wf-handoffs">
                <h3 id="wf-handoffs">How roles pass work to each other</h3>
                <p class="wf-hint">Items a year passed from one role's work (rows) to another's (columns). Darker is more.</p>
                @if (r.handoffs.rows.length) {
                <ng-container *ngTemplateOutlet="heatmap; context: { m: r.handoffs, unit: 'items a year', joiner: '→' }"></ng-container>
                <h4 class="sub-head">Largest flows</h4>
                <ol class="top-handoffs">
                    @for (h of r.topHandoffs; track $index) {
                    <li><span class="from with-context">{{ h.from }}@if (contextScope) {<app-context-graph-button [organisation]="linqset.organisation" [scope]="contextScope" [request]="{node: h.from}" [name]="h.from"></app-context-graph-button>}</span><mat-icon aria-label="to">arrow_forward</mat-icon><span class="to">{{ h.to }}</span><span class="items">{{ count(h.items) }} a year</span></li>
                    }
                </ol>
                }@else {<p class="none">No work passes between roles in the models.</p>}
            </section>

            <!-- systems by role -->
            <section class="panel" aria-labelledby="wf-systems">
                <h3 id="wf-systems">Systems each role uses</h3>
                <p class="wf-hint">Hours a year each role works with each system. Darker is more.</p>
                @if (r.systems.rows.length) {
                <ng-container *ngTemplateOutlet="heatmap; context: { m: r.systems, unit: 'hours a year', joiner: 'uses' }"></ng-container>
                }@else {<p class="none">No role is shown using a system in the models.</p>}
            </section>

            <!-- matching people to roles -->
            <section class="panel" aria-labelledby="wf-matching">
                <h3 id="wf-matching">Who could help</h3>
                <p class="wf-hint">For roles that are short of capacity or depend on one person: people with time to spare whose roles use
                    the same systems and models, so would need the least training. LINQ doesn't hold skills, so this is a guide to start
                    from: confirm skills with the people involved.</p>
                @if (r.matches.length) {
                @if (helpMatches.length) {
                <div class="matches" data-testid="workforce-matches">
                    @for (m of helpMatches; track m.roleKey) {
                    <article class="match">
                        <h4><span class="match-role with-context">{{ m.role }}@if (contextScope) {<app-context-graph-button [organisation]="linqset.organisation" [scope]="contextScope" [request]="{subject: {kind: 'role', key: m.roleKey, name: m.role}}" [name]="m.role"></app-context-graph-button>}</span><span [class]="'reason ' + m.reason">{{ m.reason === 'over' ? 'Over capacity' : 'One person' }}</span></h4>
                        <ul>
                            @for (c of m.candidates; track c.key) {
                            <li>
                                <div class="candidate"><strong>{{ c.name }}</strong><span class="fit" [class.strong]="c.similarity >= 0.5">{{ c.similarity >= 0.5 ? 'Strong fit' : 'Possible fit' }}</span></div>
                                <p>{{ c.currentRoles.join(', ') }} · {{ hours(c.spareHours) }} h a year spare
                                    @if (c.sharedSystems.length) { · uses {{ c.sharedSystems.slice(0, 3).join(', ') }}}</p>
                            </li>
                            }
                        </ul>
                    </article>
                    }
                </div>
                }
                @if (noHelpMatches.length) {
                <div class="no-help" data-testid="workforce-no-match">
                    <p>No one with spare time works with the same systems or models{{ helpMatches.length ? ' for the other ' + noHelpMatches.length + ' roles' : '' }}:</p>
                    <div class="no-help-roles">
                        @for (m of noHelpMatches; track m.roleKey) {<span [class]="'reason ' + m.reason" [matTooltip]="m.reason === 'over' ? 'Over capacity' : 'Only one person can do it'">{{ m.role }}</span>}
                    </div>
                </div>
                }
                }@else {<p class="none">No role is short of capacity or depends on a single person.</p>}
            </section>
        </div>

        <ng-template #heatmap let-m="m" let-unit="unit" let-joiner="joiner">
            <div class="scroll">
                <table class="heatmap" [attr.aria-label]="'Table of ' + unit">
                    <thead><tr><th class="corner"></th>@for (c of m.columns; track c.key) {<th scope="col"><span [title]="c.name">{{ c.name }}</span></th>}</tr></thead>
                    <tbody>
                        @for (row of m.rows; track row.key; let i = $index) {
                        <tr>
                            <th scope="row"><span [title]="row.name">{{ row.name }}</span></th>
                            @for (c of m.columns; track c.key; let j = $index) {
                            <td [style.background]="cellColour(m, m.values[i][j])" [class.dark]="isDark(m, m.values[i][j])" [class.empty]="!m.values[i][j]"
                                [matTooltip]="m.values[i][j] ? row.name + ' ' + joiner + ' ' + c.name + ': ' + count(m.values[i][j]) + ' ' + unit : ''">
                                {{ m.values[i][j] ? count(m.values[i][j]) : '' }}</td>
                            }
                        </tr>
                        }
                    </tbody>
                </table>
            </div>
        </ng-template>
        }
`,
    styles: [`
        .with-context { display: inline-flex; align-items: center; gap: 2px; min-width: 0; }
        .with-context .ellipsis { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        /* in the bar charts the icons line up at the end of the names */
        .name.with-context { display: flex; }
        .name.with-context .ellipsis { flex: 1 1 auto; }
        /* the app's palette: status colours for workload, teal for money, and the quiet greys of the other reports */
        :host { display: block; --wf-over: #d64541; --wf-stretched: #e8894f; --wf-healthy: #2f9e5b; --wf-under: #3b82c4; --wf-unknown: #a9aebb;
            --wf-used: #00a9a5; --wf-idle: #bfe9e7; --wf-ink: var(--ink, #1E2233); --wf-muted: var(--slate, #5b6172); --wf-grey: var(--grey, #9aa0b0);
            --wf-line: var(--line, #e7e9f0); --wf-bg: var(--bg, #f4f5f9); --wf-surface: var(--paper, #fff); --wf-red: #b42318; }
        .status { display: flex; align-items: center; gap: 10px; margin: 16px; color: var(--wf-muted); }
        .error { color: var(--wf-red); }
        .workforce { display: flex; flex-direction: column; gap: 16px; padding: 12px 16px 24px; color: var(--wf-ink); max-width: 1400px; container-type: inline-size; container-name: workforce; }
        h2 { margin: 0 0 4px; font-size: 20px; font-weight: 600; }
        h3 { margin: 0 0 4px; font-size: 15px; font-weight: 600; }
        h4 { margin: 0; font-size: 14px; font-weight: 600; }
        .intro p { margin: 0; color: var(--wf-muted); font-size: 13px; line-height: 1.5; max-width: 90ch; }
        .intro strong { color: var(--wf-ink); font-weight: 600; }
        .wf-hint { margin: 0 0 10px; color: var(--wf-muted); font-size: 12.5px; line-height: 1.45; max-width: 90ch; }
        .none { margin: 4px 0 0; color: var(--wf-muted); font-size: 13px; }
        .muted { color: var(--wf-grey); }

        /* sections are cards, as on the overview */
        .panel { min-width: 0; padding: 14px 16px 12px; border: 1px solid var(--wf-line); border-radius: 10px; background: var(--wf-surface); }
        .panel-head { display: flex; align-items: center; justify-content: space-between; gap: 8px 12px; flex-wrap: wrap; margin-bottom: 8px; }
        .panel-head h3 { margin: 0; }
        .more { margin-top: 6px; }
        .more mat-icon { margin-right: 2px; }

        /* the headline figures: five to a line when there's room, else three, else two with the last across both */
        .tiles { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 10px; }
        /* three to a line on six tracks, so the last two share the second line evenly */
        @container workforce (max-width: 860px) {
            .tiles { grid-template-columns: repeat(6, minmax(0, 1fr)); }
            .tile { grid-column: span 2; }
            .tile:nth-child(n+4):nth-last-child(-n+2) { grid-column: span 3; }
        }
        @container workforce (max-width: 520px) {
            .tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            .tile, .tile:nth-child(n+4):nth-last-child(-n+2) { grid-column: auto; }
            .tile:last-child:nth-child(odd) { grid-column: 1 / -1; }
        }
        .tile { display: flex; flex-direction: column; gap: 2px; min-width: 0; padding: 12px 14px; border: 1px solid var(--wf-line); border-radius: 10px; background: var(--wf-surface); }
        .tile .label { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--wf-muted); }
        .tile .label mat-icon { width: 16px; height: 16px; font-size: 16px; color: var(--wf-grey); }
        /* a project's figure against the current state's */
        .chg { align-self: flex-start; margin-top: 4px; font-size: 11px; font-weight: 700; white-space: nowrap; padding: 1px 6px; border-radius: 6px; }
        .chg.good { background: #e4f6ec; color: #1e7b45; }
        .chg.bad { background: #fbe4e2; color: #b42318; }
        .chg.flat { background: #eef0f6; color: #6b7280; }
        .tile strong { font-size: 22px; font-weight: 600; line-height: 1.25; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
        .tile .sub { font-size: 12px; color: var(--wf-muted); line-height: 1.35; }
        .tile.alert { border-color: #f1c4c0; background: #fdf3f2; }
        .tile.alert strong, .tile.alert .label mat-icon { color: var(--wf-red); }
        .status-text.over { color: var(--wf-red); }

        /* severity, as a small pill: act now in red, look at in amber, data in grey */
        .counts { display: flex; flex-wrap: wrap; gap: 6px; }
        .pill { flex: none; padding: 1px 8px; border-radius: 10px; font-size: 11.5px; font-weight: 600; white-space: nowrap; background: var(--wf-bg); color: var(--wf-muted); }
        .pill.critical { background: #fde8e8; color: var(--wf-red); }
        .pill.warning { background: #fdf3dd; color: #8a5a00; }

        /* findings: a compact list, marked by an edge in their severity's colour */
        .findings { list-style: none; margin: 0; padding: 0; }
        .finding { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; gap: 10px; align-items: start; padding: 9px 10px 9px 8px; border-left: 3px solid var(--wf-unknown); border-bottom: 1px solid var(--wf-line); }
        .finding:last-child { border-bottom: 0; }
        .finding mat-icon { width: 20px; height: 20px; font-size: 20px; margin-top: 1px; color: var(--wf-grey); }
        .finding.critical { border-left-color: var(--wf-over); } .finding.critical mat-icon { color: var(--wf-over); }
        .finding.warning { border-left-color: #f0b429; } .finding.warning mat-icon { color: #c98500; }
        .finding strong { display: block; font-size: 13.5px; font-weight: 600; line-height: 1.35; }
        .finding p { margin: 2px 0 0; font-size: 12.5px; line-height: 1.45; color: var(--wf-muted); }
        @container workforce (max-width: 520px) { .finding > .pill { display: none; } }

        /* the two bar charts, side by side when the report itself is wide enough, whatever the window */
        .two { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
        @container workforce (max-width: 900px) { .two { grid-template-columns: minmax(0, 1fr); } }
        .legend { display: flex; flex-wrap: wrap; gap: 6px 14px; font-size: 12px; color: var(--wf-muted); margin-bottom: 10px; }
        .legend span { display: inline-flex; align-items: center; gap: 5px; }
        .swatch { display: inline-block; width: 10px; height: 10px; border-radius: 3px; }
        .bars { list-style: none; margin: 0; padding: 0; display: grid; gap: 2px; }
        .bars li { display: grid; grid-template-columns: minmax(90px, 36%) minmax(0, 1fr) 56px; align-items: center; gap: 10px; min-height: 26px; padding: 0 4px; border-radius: 6px; font-size: 13px; }
        .bars li:hover { background: var(--wf-bg); }
        .bars li.empty-row { display: block; }
        .bars .name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .bars .value { text-align: right; font-variant-numeric: tabular-nums; color: var(--wf-muted); font-size: 12.5px; }
        .bars .value.over { color: var(--wf-red); font-weight: 600; }
        .track { position: relative; height: 10px; background: var(--wf-bg); border-radius: 5px; }
        .track .fill { position: absolute; top: 0; bottom: 0; left: 0; border-radius: 5px; }
        .track .fill.idle { border-left: 2px solid var(--wf-surface); }
        .track .capacity { position: absolute; top: -4px; bottom: -4px; width: 2px; margin-left: -1px; border-radius: 1px; background: var(--wf-ink); opacity: .55; }
        .fill.over, .swatch.over { background-color: var(--wf-over); } .fill.stretched, .swatch.stretched { background-color: var(--wf-stretched); }
        .fill.healthy, .swatch.healthy { background-color: var(--wf-healthy); } .fill.under, .swatch.under { background-color: var(--wf-under); }
        .fill.unknown, .swatch.unknown { background-color: var(--wf-unknown); }
        /* a role's work in the organisation's other models: its status colour, striped, after its work here */
        .track .fill.other { border-left: 2px solid var(--wf-surface); background-image: repeating-linear-gradient(135deg, rgba(255,255,255,.6) 0 3px, transparent 3px 6px); }
        .swatch.here { background: var(--wf-unknown); }
        .swatch.other { background-color: var(--wf-unknown); background-image: repeating-linear-gradient(135deg, rgba(255,255,255,.6) 0 2px, transparent 2px 4px); }
        .fill.used, .swatch.used { background-color: var(--wf-used); } .fill.idle, .swatch.idle { background-color: var(--wf-idle); } .fill.exception, .swatch.exception { background-color: #e0a526; }
        .default-mark { margin-left: 2px; color: var(--wf-muted); }
        /* very narrow (the insights panel beside a model): less padding, and each name above its bar */
        @container workforce (max-width: 380px) {
            .panel { padding: 12px; }
            .bars { gap: 6px; }
            .bars li { grid-template-columns: minmax(0, 1fr) 48px; gap: 2px 8px; padding: 2px 0; }
            .bars .name { grid-column: 1 / -1; font-size: 12.5px; }
            .tile { padding: 10px 12px; }
            .tile strong { font-size: 19px; }
            .panel .heatmap tbody th span { max-width: 96px; }
            .panel .matches { grid-template-columns: minmax(0, 1fr); }
        }

        /* people: the quiet table style the other insights tables use */
        .scroll { overflow-x: auto; max-width: 100%; }
        table { border-collapse: collapse; font-size: 13px; }
        .people { width: 100%; min-width: 720px; }
        .people th { height: 36px; padding: 0 10px; background: var(--wf-bg); border-bottom: 1px solid var(--wf-line); color: var(--wf-muted);
            font-size: 11.5px; font-weight: 600; letter-spacing: .03em; text-transform: uppercase; text-align: left; white-space: nowrap; }
        .people th.num { text-align: right; }
        .people td { height: 40px; padding: 4px 10px; border-bottom: 1px solid var(--wf-line); vertical-align: middle; }
        .people tbody tr:hover td { background: #f8f9fb; }
        .people tbody tr:last-child td { border-bottom: 0; }
        .person { font-weight: 500; }
        .values tfoot td { height: 36px; padding: 4px 10px; border-top: 1px solid var(--wf-line); font-weight: 600; }
        .values td.low { color: var(--wf-red); }
        .nowrap { white-space: nowrap; }
        .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .chip, .tag, .flag { display: inline-flex; align-items: center; gap: 4px; margin: 1px 4px 1px 0; padding: 1px 8px; border-radius: 6px; font-size: 12px; line-height: 1.6; white-space: nowrap; }
        .chip { background: var(--wf-bg); border: 1px solid var(--wf-line); color: var(--wf-ink); }
        .chip .share { color: var(--wf-muted); font-variant-numeric: tabular-nums; }
        .tag { margin-left: 6px; background: var(--teal-soft, #e0f5f4); color: #00706d; }
        .flag { border-radius: 10px; background: #fdf3dd; color: #8a5a00; font-weight: 500; }
        .flag.serious { background: #fde8e8; color: var(--wf-red); }
        .mini { display: inline-block; position: relative; width: 64px; height: 8px; background: var(--wf-bg); border-radius: 4px; vertical-align: middle; overflow: hidden; }
        .mini .fill { position: absolute; top: 0; bottom: 0; left: 0; border-radius: 4px; }
        .mini-value { margin-left: 8px; font-variant-numeric: tabular-nums; }
        .mini-value.over { color: var(--wf-red); font-weight: 600; }

        /* heatmaps: names kept in view while the grid scrolls, column names over two lines, empty cells faintly marked */
        .heatmap { border-collapse: separate; border-spacing: 3px; margin: 0 -3px; }
        .heatmap th { font-weight: 500; font-size: 12px; color: var(--wf-muted); padding: 2px 4px; }
        .heatmap thead th { vertical-align: bottom; min-width: 56px; max-width: 120px; }
        .heatmap thead th span { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; line-height: 1.25; text-align: center; overflow-wrap: normal; word-break: normal; }
        .heatmap tbody th { position: sticky; left: 0; z-index: 1; text-align: left; color: var(--wf-ink); background: var(--wf-surface); }
        .heatmap thead th.corner { position: sticky; left: 0; z-index: 1; background: var(--wf-surface); }
        .heatmap tbody th span { display: block; max-width: 170px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .heatmap td { min-width: 56px; height: 30px; text-align: center; font-size: 11.5px; font-variant-numeric: tabular-nums; border-radius: 5px; color: #0b0b0b; }
        .heatmap td.empty { background: #f7f8fa !important; }
        .heatmap td.dark { color: #fff; }
        .sub-head { margin: 14px 0 6px; font-size: 13px; color: var(--wf-muted); }
        .top-handoffs { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(min(280px, 100%), 1fr)); gap: 4px 16px; counter-reset: flow; }
        .top-handoffs li { display: flex; align-items: center; gap: 6px; min-width: 0; padding: 5px 0; border-bottom: 1px solid var(--wf-line); font-size: 13px; counter-increment: flow; }
        .top-handoffs li::before { content: counter(flow); flex: none; width: 16px; color: var(--wf-grey); font-size: 12px; font-variant-numeric: tabular-nums; }
        .top-handoffs .from, .top-handoffs .to { flex: 0 1 auto; min-width: 3em; max-width: 42%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; }
        .top-handoffs mat-icon { flex: none; width: 16px; height: 16px; font-size: 16px; color: var(--wf-grey); }
        .top-handoffs .items { margin-left: auto; flex: none; color: var(--wf-muted); font-size: 12px; font-variant-numeric: tabular-nums; }

        /* who could help: cards for the roles someone could help with, then the rest in a line */
        .matches { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 10px; }
        .match { min-width: 0; padding: 12px; border: 1px solid var(--wf-line); border-radius: 10px; background: var(--wf-surface); }
        .match h4 { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; margin-bottom: 6px; }
        .match-role { min-width: 0; overflow-wrap: anywhere; }
        .match ul { list-style: none; margin: 0; padding: 0; }
        .match li { padding: 7px 0; border-top: 1px solid var(--wf-line); }
        .candidate { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
        .match li p { margin: 2px 0 0; font-size: 12px; color: var(--wf-muted); overflow-wrap: anywhere; }
        .reason { flex: none; font-size: 11.5px; font-weight: 600; padding: 1px 8px; border-radius: 10px; white-space: nowrap; }
        .reason.over { background: #fde8e8; color: var(--wf-red); } .reason.single { background: #fdf3dd; color: #8a5a00; }
        .fit { flex: none; font-size: 11.5px; color: var(--wf-muted); }
        .fit.strong { color: #00706d; font-weight: 600; }
        .no-help { margin-top: 10px; padding: 10px 12px; border-radius: 8px; background: var(--wf-bg); }
        .no-help p { margin: 0 0 8px; font-size: 12.5px; color: var(--wf-muted); }
        .no-help-roles { display: flex; flex-wrap: wrap; gap: 6px; }
        .no-help-roles .reason { flex: 0 1 auto; white-space: normal; overflow-wrap: anywhere; }
`],
})
export class WorkforceReportComponent implements OnChanges {
    @Input() linqset: Linqset;
    @Input() trigger: any;                           // the insights were refreshed
    @Input() prebuilt?: WorkforceReport;
    @Input() baseline?: WorkforceReport;             // in a project, the same report for the current state, which its figures are compared with

    // the models looked at, for the context graphs of its roles
    get contextScope(): AnalysisScope | undefined {
        return this.linqset ? scopeForLinqset(this.linqset) : undefined;
    }             // a report already built, as the People & roles lens has, so it isn't built twice

    readonly statuses: WorkforceStatus[] = ['over', 'stretched', 'healthy', 'under'];
    report?: WorkforceReport;
    loading = false;
    error = '';
    allFindings = false;
    allRoles = false;
    peopleToLookAt = false;
    knownRoles: RoleSummary[] = [];
    scopeName = '';                                  // the model, or how many models, the report is for when it isn't the whole organisation
    scopeModels = 0;
    hasOtherWork = false;                            // some role shown also works in models outside the scope
    severityCounts: { severity: FindingSeverity; count: number }[] = [];   // how many findings of each severity, most severe first
    helpMatches: WorkforceMatch[] = [];              // roles someone could help with
    noHelpMatches: WorkforceMatch[] = [];            // roles no one with spare time is a match for
    costRoles: RoleSummary[] = [];
    valueRoles: RoleSummary[] = [];                  // roles whose work creates value or revenue, the most first
    private maxUtilisation = 1;
    private maxCost = 0;
    private request = 0;

    constructor(private organisationService: OrganisationService, private insightsService: InsightsService, private repoService: RepositoryService,
        private formatter: FormattingService) {}

    ngOnChanges(changes: SimpleChanges) {
        if (this.prebuilt) {
            if (changes['prebuilt']) this.show(this.prebuilt);
            return;
        }
        if (changes['linqset'] || changes['trigger']) void this.load();
    }

    // the people and roles doing the work in what's being looked at: a model being edited, the models the insights are
    // filtered to, or the whole organisation. As the analysis views do, a scope keeps just the work in its models
    private async load() {
        const organisation = this.organisationService.organisation;
        if (!organisation || !this.linqset) return;
        const workspaceKey = this.linqset.workspaceKey;
        const scope = scopeForLinqset(this.linqset);
        const scoped = !isWholeOrganisation(scope);
        const models = scoped ? modelsInScope(scope, organisation) : [];
        const modelKeys = new Set(models.map(m => m._key));
        const request = ++this.request;
        this.loading = true;
        this.error = '';
        try {
            const [analysis, organisationModel] = await Promise.all([
                this.insightsService.loadAnalysisGraphForFilter(organisation, createInsightsFilter(workspaceKey)) as Promise<{ nodes: AnalysisNode[]; edges: AnalysisEdge[] }>,
                this.insightsService.afterRefresh(workspaceKey, () => this.repoService.callSingleAPI(this.repoService.getData<GraphData>(MODEL_API(organisation._key, ORGANISATION_MODEL_KEY, workspaceKey)), COMPONENT, 'Load the organisation model')),
            ]);
            if (request !== this.request) return;
            const currency = getCurrency(organisation.currencyCode || DEFAULT_CURRENCY);
            const inScope = scoped ? { nodes: analysis.nodes.filter(n => modelKeys.has(n.modelKey)), edges: analysis.edges.filter(e => modelKeys.has(e.modelKey)) } : analysis;
            this.scopeName = !scoped ? '' : models.length === 1 ? models[0].name : `${models.length} models`;
            this.scopeModels = models.length;
            this.show(buildWorkforceReport({
                organisationModel, analysis: inScope, workspaceKey, scoped,
                modelNames: Object.fromEntries(organisation.models.map(m => [m._key, m.name])),
                defaultCapacityHours: defaultCapacityHours(organisation),
                money: v => this.formatter.formatAbbreviatedNumber(v, currency),
                number: v => this.formatter.formatAbbreviatedNumber(v),
            }));
        }catch(err) {
            if (request !== this.request) return;
            console.error('Unable to build the workforce report', err);
            this.error = "The workforce report couldn't be built. Please try again.";
        }finally {
            if (request === this.request) this.loading = false;
        }
    }

    private show(report: WorkforceReport) {
        this.report = report;
        this.knownRoles = report.roles.filter(r => r.utilisation !== null && !r.external);
        this.hasOtherWork = this.knownRoles.some(r => r.otherHours > 0);
        this.severityCounts = (['critical', 'warning', 'info'] as FindingSeverity[])
            .map(severity => ({ severity, count: report.findings.filter(f => f.severity === severity).length })).filter(c => c.count > 0);
        this.helpMatches = report.matches.filter(m => m.candidates.length > 0);
        this.noHelpMatches = report.matches.filter(m => m.candidates.length === 0);
        this.maxUtilisation = Math.max(1.2, ...this.knownRoles.map(r => r.utilisation || 0));
        this.costRoles = report.roles.filter(r => !r.external && r.cost > 0).sort((a, b) => b.cost - a.cost).slice(0, 12);
        this.maxCost = Math.max(0, ...this.costRoles.map(r => r.cost));
        this.valueRoles = report.roles.filter(r => r.contribution.value > 0 || r.contribution.revenue > 0)
            .sort((a, b) => b.contribution.revenue - a.contribution.revenue || b.contribution.value - a.contribution.value);
    }

    get shownPeople(): PersonSummary[] {
        const people = this.report?.people || [];
        return this.peopleToLookAt ? people.filter(p => p.flags.length) : people;
    }

    // a headline figure against the current state's, when a project is looked at
    vs(figure: keyof ReturnType<typeof workforceFigures>): Change | undefined {
        if (!this.baseline || !this.report) return undefined;
        return changeFrom(workforceFigures(this.baseline)[figure], workforceFigures(this.report)[figure], changeFormat(v => this.money(v), v => this.count(v)));
    }

    money(value: number) {
        return this.formatter.formatAbbreviatedNumber(value, getCurrency(this.organisationService.organisation?.currencyCode || DEFAULT_CURRENCY));
    }

    hours(value: number) {
        return this.formatter.formatAbbreviatedNumber(value);
    }

    count(value: number) {
        return this.formatter.formatAbbreviatedNumber(value);
    }

    severity(s: FindingSeverity) {
        return SEVERITY[s];
    }

    statusLabel(s: WorkforceStatus) {
        return WORKFORCE_STATUS_LABELS[s];
    }

    overallStatus(r: WorkforceReport) {
        return r.totals.utilisation === null ? 'unknown' : r.totals.utilisation > 1 ? 'over' : 'healthy';
    }

    // a role's bar, scaled so the busiest role fits and 100% sits at the same place for every role
    barWidth(utilisation: number | null) {
        return Math.min(100, ((utilisation || 0) / this.maxUtilisation) * 100);
    }

    miniWidth(utilisation: number) {
        return Math.min(100, utilisation * 100 / 1.5);
    }

    get currencySymbol() {
        return getCurrency(this.organisationService.organisation?.currencyCode || DEFAULT_CURRENCY).symbol || '';
    }

    share(value: number, of: number) {
        return of > 0 ? Math.min(100, Math.max(0, value) / of * 100) : 0;
    }

    costWidth(value: number) {
        return this.maxCost ? Math.max(0, value) / this.maxCost * 100 : 0;
    }

    // what the solid part of the bars is: the work in the model, or models, being looked at
    get hereLabel() {
        return this.scopeModels === 1 ? 'In this model' : this.scopeModels > 1 ? 'In these models' : 'In the models shown';
    }

    // the role's work in the scope, as a share of its capacity
    hereShare(role: RoleSummary) {
        return role.capacity ? role.hours / role.capacity : role.utilisation;
    }

    roleTip(role: RoleSummary) {
        const exception = role.frictionActions ? `, ${this.hours(role.frictionHours)} of them friction work` : '';
        if (role.otherHours) return `${role.name}: ${this.hours(role.hours)} hours of work a year ${this.hereLabel.toLowerCase()}${exception} and ${this.hours(role.otherHours)} in other models, ${this.hours(role.hours + role.otherHours)} against ${this.hours(role.capacity || 0)} of capacity${role.capacityFromDefault ? " (the organisation's full-time year)" : ''} (${this.statusLabel(role.status)}), ${role.headcount} ${role.headcount === 1 ? 'person' : 'people'}`;
        return `${role.name}: ${this.hours(role.hours)} hours of work a year${exception} against ${this.hours(role.capacity || 0)} of capacity${role.capacityFromDefault ? " (the organisation's full-time year)" : ''} (${this.statusLabel(role.status)}), ${role.headcount} ${role.headcount === 1 ? 'person' : 'people'}`;
    }

    // a cell's shade on the ramp, by its share of the largest (square-rooted, so smaller flows still show)
    private step(m: WorkforceMatrix, value: number) {
        return value && m.max ? Math.min(RAMP.length - 1, Math.floor(Math.sqrt(value / m.max) * RAMP.length)) : -1;
    }

    cellColour(m: WorkforceMatrix, value: number) {
        const step = this.step(m, value);
        return step < 0 ? 'transparent' : RAMP[step];
    }

    isDark(m: WorkforceMatrix, value: number) {
        return this.step(m, value) >= 3;
    }
}
